/**
 * Widget registry for platform overview — order and page keys for permission gating.
 * Keep in sync with supabase/functions/_shared/platformOverviewEntitlements.ts
 */

export const OVERVIEW_WIDGET_IDS = {
  globalKpis: 'global-kpis',
  operations: 'operations',
  clients: 'clients',
  analytics: 'analytics',
  raceTracker: 'race-tracker',
  adimpact: 'adimpact',
  resources: 'resources',
  elections: 'elections',
  primaryCalendar: 'primary-calendar',
  bigPoliticalList: 'big-political-list',
  ideaBoard: 'idea-board',
  knowledgeSearch: 'knowledge-search',
  personalLibrary: 'personal-library',
  basecamp: 'basecamp',
  aiAgents: 'ai-agents',
  aiLogs: 'ai-logs',
  adminShortcuts: 'admin-shortcuts',
}

/** @type {Array<{ id: string; pageKeys: string[]; priority: number; label?: string; href?: string; isModule?: boolean; special?: 'personal' }>} */
export const OVERVIEW_WIDGET_REGISTRY = [
  { id: OVERVIEW_WIDGET_IDS.globalKpis, pageKeys: ['clients'], priority: 10 },
  {
    id: OVERVIEW_WIDGET_IDS.operations,
    pageKeys: ['ai-agents-logs', 'basecamp-projects', 'basecamp-knowledge'],
    priority: 20,
  },
  {
    id: OVERVIEW_WIDGET_IDS.clients,
    pageKeys: ['clients'],
    priority: 30,
    label: 'Clients',
    href: '/?tab=clients',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.analytics,
    pageKeys: ['analytics'],
    priority: 40,
    label: 'Analytics',
    href: '/?tab=analytics',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.raceTracker,
    pageKeys: ['race-tracker'],
    priority: 50,
    label: 'Race Tracker',
    href: '/race-tracker',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.adimpact,
    pageKeys: ['adimpact-alerts'],
    priority: 60,
    label: 'AdImpact Alerts',
    href: '/?tab=adimpact-alerts',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.resources,
    pageKeys: ['resources'],
    priority: 70,
    label: 'Resources',
    href: '/?tab=resources',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.elections,
    pageKeys: ['elections'],
    priority: 75,
    label: 'Election Data',
    href: '/?tab=elections',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.primaryCalendar,
    pageKeys: ['primary-calendar'],
    priority: 76,
    label: 'Primary Calendar',
    href: '/?tab=primary-calendar',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.bigPoliticalList,
    pageKeys: ['big-political-list'],
    priority: 77,
    label: 'Big Political List',
    href: '/?tab=big-political-list',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.ideaBoard,
    pageKeys: ['idea-board'],
    priority: 78,
    label: 'Idea Board',
    href: '/?tab=idea-board',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.knowledgeSearch,
    pageKeys: [
      'knowledge-search',
      'knowledge-base',
      'knowledge-base:dashboard',
      'knowledge-base:content',
      'knowledge-base:access',
      'knowledge-base:wiki',
    ],
    priority: 79,
    label: 'Knowledge Search',
    href: '/knowledge',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.personalLibrary,
    pageKeys: [],
    priority: 80,
    label: 'Personal Library',
    href: '/personal-knowledge',
    isModule: true,
    special: 'personal',
  },
  {
    id: OVERVIEW_WIDGET_IDS.basecamp,
    pageKeys: ['basecamp-projects', 'basecamp-knowledge'],
    priority: 85,
    label: 'Basecamp',
    href: '/admin/basecamp/projects',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.aiAgents,
    pageKeys: ['ai-agents'],
    priority: 90,
    label: 'AI Agents',
    href: '/admin/agents-settings',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.aiLogs,
    pageKeys: ['ai-agents-logs'],
    priority: 100,
    label: 'AI Logs',
    href: '/admin/ai/agents-logs',
    isModule: true,
  },
  {
    id: OVERVIEW_WIDGET_IDS.adminShortcuts,
    pageKeys: [
      'settings-users',
      'settings-integrations',
      'basecamp-knowledge',
      'basecamp-projects',
      'ai-agents',
      'ai-agents-logs',
      'technical-documentation',
      'basecamp-api-references',
    ],
    priority: 110,
  },
]

/**
 * @param {(key: string) => boolean} canAccessPage
 * @param {{ personalLibraryEnabled?: boolean }} [opts]
 * @returns {Set<string>}
 */
export function getVisibleOverviewWidgetIds(canAccessPage, opts = {}) {
  const visible = new Set()
  for (const w of OVERVIEW_WIDGET_REGISTRY) {
    if (w.special === 'personal') {
      if (opts.personalLibraryEnabled) visible.add(w.id)
      continue
    }
    if (w.pageKeys.length === 0) continue
    if (w.pageKeys.some((k) => canAccessPage(k))) {
      visible.add(w.id)
    }
  }
  return visible
}
